import React, { useState } from 'react';
type DeviceVariant = 'browser' | 'laptop' | 'phone';
interface DeviceFrameProps {
  variant?: DeviceVariant;
  /** Image source. Omit when passing custom children (e.g. a native illustrative UI). */
  src?: string;
  alt?: string;
  /**
   * Decorative chrome label (not a live URL). Prefer non-navigable copy like
   * "Prototype · Reader path" so visitors are not tempted to visit it.
   */
  chromeLabel?: string;
  /** @deprecated Use chromeLabel. Kept for call sites that still pass url=. */
  url?: string;
  /** Aspect ratio for the screen area, e.g. "16 / 10". Prevents layout shift. */
  aspect?: string;
  /** Below-the-fold images should lazy-load. Hero/above-the-fold can pass "eager". */
  loading?: 'eager' | 'lazy';
  className?: string;
  children?: React.ReactNode;
  /** object-position for cropping full-page screenshots to their strongest region. */
  objectPosition?: string;
}
function BrowserChrome({
  label
}: {
  label: string;
}) {
  return <div className="flex items-center gap-2 border-b border-gray-200/80 bg-paper-dark px-3 py-2 sm:px-4 sm:py-2.5" data-id="element-633" data-mp="8arwv6brepkx">
      <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57] sm:h-3 sm:w-3" aria-hidden="true" data-id="element-634" data-mp="ccv8pw2cbw6q" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e] sm:h-3 sm:w-3" aria-hidden="true" data-id="element-635" data-mp="3uihxp31ip6nt" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#28c840] sm:h-3 sm:w-3" aria-hidden="true" data-id="element-636" data-mp="3b5eyjkzkl5s" />
      <div className="ml-2 flex min-w-0 flex-1 select-none items-center rounded-md bg-white px-2.5 py-1 text-[0.7rem] font-medium text-text-muted sm:ml-3 sm:px-3 sm:text-xs" aria-hidden="true" data-id="element-637" data-mp="r0q0na8ixwir">
        <span className="truncate" data-id="element-638" data-mp="hbpwzdj2ucy8">{label}</span>
      </div>
    </div>;
}
function Screen({
  src,
  alt,
  aspect,
  loading = 'lazy',
  objectPosition,
  children
}: Pick<DeviceFrameProps, 'src' | 'alt' | 'aspect' | 'loading' | 'objectPosition' | 'children'>) {
  const [errored, setErrored] = useState(false);
  const pendingFile = src ? src.split('/').pop() : undefined;
  return <div className="relative w-full overflow-hidden bg-white" style={src && aspect ? {
    aspectRatio: aspect
  } : undefined} data-id="element-639" data-mp="1esnef6vjaymi">
      {src && !errored ? <img src={src} alt={alt ?? ''} loading={loading} decoding="async" onError={() => setErrored(true)} className="h-full w-full object-cover object-top" style={objectPosition ? {
      objectPosition
    } : undefined} data-id="element-640" data-mp="1jorexk67a59g" /> : src && errored ? <div className="flex h-full w-full flex-col items-center justify-center gap-2 border border-dashed border-gray-300 bg-paper-dark p-6 text-center" data-id="element-641" data-mp="1ap4ccv1qitet">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted" data-id="element-642" data-mp="10w70h7r902p6">
            Real proof asset pending
          </p>
          <code className="rounded bg-white px-2 py-1 text-[0.7rem] text-navy ring-1 ring-gray-200" data-id="element-643" data-mp="vw2jlgmfh5yf">
            public/proof/{pendingFile}
          </code>
        </div> : children}
    </div>;
}
export function DeviceFrame({
  variant = 'browser',
  src,
  alt,
  chromeLabel,
  url,
  aspect = '16 / 10',
  loading = 'lazy',
  className = '',
  children,
  objectPosition
}: DeviceFrameProps) {
  const label = chromeLabel ?? url ?? 'Prototype · Reader path';
  if (variant === 'phone') {
    return <div className={`relative mx-auto w-full max-w-[300px] rounded-[2.5rem] border-[10px] border-navy bg-navy p-0 shadow-[0_40px_80px_-30px_rgba(26,40,56,0.55)] ${className}`} data-id="element-644" data-mp="2yizi4noos2i3">
        <div className="absolute left-1/2 top-2.5 z-10 h-1.5 w-16 -translate-x-1/2 rounded-full bg-white/25" data-id="element-645" data-mp="37kywt9c8auhv" />
        <div className="overflow-hidden rounded-[1.9rem]" data-id="element-646" data-mp="2ax2qcxai9fkd">
          <Screen src={src} alt={alt} aspect={aspect ?? '9 / 19'} loading={loading} objectPosition={objectPosition} data-id="element-647" data-mp="2ew1s690xww7l">
            {children}
          </Screen>
        </div>
      </div>;
  }
  if (variant === 'laptop') {
    return <div className={`mx-auto w-full ${className}`} data-id="element-648" data-mp="2oneiuxb05gc1">
        <div className="rounded-t-2xl border-[3px] border-b-0 border-navy bg-navy p-2 shadow-[0_40px_80px_-30px_rgba(26,40,56,0.5)]" data-id="element-649" data-mp="1r7ihjmuf47cc">
          <div className="overflow-hidden rounded-lg bg-white" data-id="element-650" data-mp="1w5ac6omg67k8">
            <BrowserChrome label={label} data-id="element-651" data-mp="2nczewegsg01" />
            <Screen src={src} alt={alt} aspect={aspect} loading={loading} objectPosition={objectPosition} data-id="element-652" data-mp="2oe9oynnoygp">
              {children}
            </Screen>
          </div>
        </div>
        <div className="relative mx-auto h-3 w-[112%] -translate-x-[5.4%] rounded-b-2xl bg-navy-light" data-id="element-653" data-mp="bgyeyhyge1ai">
          <div className="absolute left-1/2 top-0 h-1.5 w-24 -translate-x-1/2 rounded-b-lg bg-navy" data-id="element-654" data-mp="6o58npnc6kwb" />
        </div>
      </div>;
  }

  // browser
  return <div className={`overflow-hidden rounded-2xl border border-white/10 bg-white shadow-[0_40px_80px_-30px_rgba(26,40,56,0.55)] ring-1 ring-black/5 ${className}`} data-id="element-655" data-mp="lfxf1c55kolw">
      <BrowserChrome label={label} data-id="element-656" data-mp="ue92546t949l" />
      <Screen src={src} alt={alt} aspect={aspect} loading={loading} objectPosition={objectPosition} data-id="element-657" data-mp="qd7c9dysa4ka">
        {children}
      </Screen>
    </div>;
}